import { useEffect, useMemo, useState } from "react";
import { BellRing, CheckCircle2, Info, Mail, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { format } from "date-fns";

import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import {
  FilterSelect,
  INVESTOR_LABEL,
  SCOPE_LABEL,
  ScopeToggle,
  concentrationFacilityParam,
  useFacilityOptions,
  useInvestorFilterOptions,
  type FacilityFilter,
  type Investor,
  type Scope,
} from "../../../components/portfolio-risk-concentration/PrcFilterBar";
// Same shell Manage Limits and Manage Capital use — it is the one that renders the "← Back"
// link above the page title. Despite the name it is not limits-specific; it is the layout
// every SUB-page of a step uses, and all three of them now do.
import {
  scopeOfInvestor,
  useBookLabels,
  useInvestorScopes,
} from "../../../utils/portfolio-risk-concentration/api/facilities";
import { ManageLimitsShell } from "../../../components/portfolio-risk-concentration/StepShell";
import { pct, thr } from "../../../components/portfolio-risk-concentration/format";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../ui/accordion";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Checkbox } from "../../../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../../ui/dialog";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Switch } from "../../../ui/switch";
import { Textarea } from "../../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";
import {
  useAlert,
  useAlertRecipients,
  useAlertsConfig,
  useAlertsMeta,
  useCreateAlert,
  useSetAlertActive,
  useUpdateAlert,
  useDeleteAlert,
  type AlertConfigInput,
  type AlertsMeta,
  type SupersetAlert,
} from "../../../utils/portfolio-risk-concentration/api/alerts-config";
import { useConcentration } from "../../../utils/portfolio-risk-concentration/api/concentration";
import type { ConcentrationRow } from "../../../utils/portfolio-risk-concentration/api/types";

// GMT-labelled timezones (as Superset shows them) mapped to the IANA names the backend needs.
const TIMEZONES: { label: string; value: string }[] = [
  { label: "GMT +00:00 (GMT Standard Time)", value: "UTC" },
  { label: "GMT +00:00 (Greenwich Mean Time)", value: "Europe/London" },
  { label: "GMT -05:00 (Eastern Standard Time)", value: "America/New_York" },
  { label: "GMT -06:00 (Central Standard Time)", value: "America/Chicago" },
  { label: "GMT -07:00 (Mountain Standard Time)", value: "America/Denver" },
  { label: "GMT -08:00 (Pacific Standard Time)", value: "America/Los_Angeles" },
  { label: "GMT +05:30 (India Standard Time)", value: "Asia/Kolkata" },
];

const OPERATORS: NonNullable<AlertConfigInput["operator"]>[] = ["<", "<=", ">", ">=", "==", "!="];
const LOG_RETENTION_DAYS = [30, 60, 90, 180, 365];
const SCHEDULE_UNITS = ["hour", "day", "week", "month"] as const;
/** "Every hour" reads as a frequency; the bare unit reads as a noun. Simple view only —
 *  the advanced view keeps the original "Every [unit]" phrasing it was built around. */
const SCHEDULE_LABEL: Record<(typeof SCHEDULE_UNITS)[number], string> = {
  hour: "Hourly",
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};
/** Short label for the simple view, which shows the timezone but cannot change it.
 *  The full GMT-offset labels in TIMEZONES are too long to sit inline beside HH:MM. */
const TZ_SHORT: Record<string, string> = {
  UTC: "UTC",
  "Europe/London": "GMT",
  "America/New_York": "ET",
  "America/Chicago": "CT",
  "America/Denver": "MT",
  "America/Los_Angeles": "PT",
  "Asia/Kolkata": "IST",
};

const WEEKDAYS = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

type ScheduleUnit = (typeof SCHEDULE_UNITS)[number];
interface ScheduleState {
  unit: ScheduleUnit;
  hour: number;
  minute: number;
  weekday: number;
  monthday: number;
}
const DEFAULT_SCHEDULE: ScheduleState = { unit: "day", hour: 0, minute: 0, weekday: 1, monthday: 1 };

function buildCron(s: ScheduleState): string {
  switch (s.unit) {
    case "hour":
      return `${s.minute} * * * *`;
    case "week":
      return `${s.minute} ${s.hour} * * ${s.weekday}`;
    case "month":
      return `${s.minute} ${s.hour} ${s.monthday} * *`;
    default:
      return `${s.minute} ${s.hour} * * *`;
  }
}

/**
 * The inverse of buildCron, for prefilling the schedule controls when editing.
 *
 * Only the four shapes buildCron emits are recognised. Anything else — a cron authored in
 * Superset directly, a step or a list — falls back to the default and the raw string stays
 * on the form's `crontab`, which the Advanced view shows and edits. Guessing a unit from a
 * cron this UI cannot express would silently rewrite someone's schedule on the next save.
 */
function parseCron(crontab?: string): ScheduleState {
  const parts = (crontab ?? "").trim().split(/\s+/);
  if (parts.length !== 5) return DEFAULT_SCHEDULE;
  const [min, hr, dom, mon, dow] = parts;
  const num = (v: string, fallback: number) => (/^\d+$/.test(v) ? Number(v) : fallback);
  if (mon !== "*") return DEFAULT_SCHEDULE;
  if (hr === "*" && dom === "*" && dow === "*")
    return { ...DEFAULT_SCHEDULE, unit: "hour", minute: num(min, 0) };
  if (dom === "*" && /^\d+$/.test(dow))
    return { ...DEFAULT_SCHEDULE, unit: "week", minute: num(min, 0), hour: num(hr, 0), weekday: Number(dow) };
  if (/^\d+$/.test(dom) && dow === "*")
    return { ...DEFAULT_SCHEDULE, unit: "month", minute: num(min, 0), hour: num(hr, 0), monthday: Number(dom) };
  if (dom === "*" && dow === "*")
    return { ...DEFAULT_SCHEDULE, unit: "day", minute: num(min, 0), hour: num(hr, 0) };
  return DEFAULT_SCHEDULE;
}

/**
 * The value the "Alert on" select carries: facility plus limit id, in one string,
 * because a Select item value has to be a string.
 *
 * ANY_LIMIT is a sentinel rather than "" because Radix reserves the empty string for
 * "nothing selected" and refuses it as an item value. It stands for the unnarrowed alert
 * — unnarrowed WITHIN the scope filters, which is the change here: it used to mean "every
 * limit in both external books" and nothing else, because that was the only scope an alert
 * could have.
 */
const ANY_LIMIT = "__all__";

function subjectValue(row: ConcentrationRow): string {
  return `${row.facilityCode}|${row.limitId}`;
}

/**
 * The scope an alert watches, as the three filters express it.
 *
 * Separate from the form because the filters do two jobs at once: they narrow the list of
 * limits below, and — when no single limit is picked — they ARE the alert. "Any limit,
 * Internal, Navitas" is a real alert on Navitas' whole internal appetite, which the page
 * could not express at all before.
 */
interface AlertScope {
  scope: Scope;
  facility: FacilityFilter;
  investor: Investor;
}

const DEFAULT_SCOPE: AlertScope = {
  // External / all facilities / all investors reproduces EXACTLY the alert this form built
  // before the filters existed: no contract_type clause, no investor clause, both books.
  // A new alert therefore still defaults to the whole external book.
  scope: "EXTERNAL",
  facility: "ALL",
  investor: "ALL",
};

/**
 * The investor a picked row pins the alert to, regardless of the filter — or undefined
 * when the row cannot pin one.
 *
 * Only internal single limits can. The internal condition matches on limit_description
 * ALONE — v_prc_concentrations_all carries no limit_id — so two investors holding the same
 * description would give one alert that counts both. Today they do not: sql/74_ gives
 * Navitas six limits of its own, each suffixed "(Navitas)". But that suffix is a naming
 * convention in a seed file, not a constraint — limit_desc is free text and Manage limits
 * will accept anything — so this pins the investor rather than trusting it to stay true.
 *
 * External rows carry investor 'WAFRA' by construction and their condition has no investor
 * clause to put it in; a joint's investor is its members', which its joint_id already pins.
 */
function pinnedInvestor(row: ConcentrationRow): AlertConfigInput["investor"] {
  if (row.isJoint || row.facilityCode !== "INTERNAL") return undefined;
  return row.investor === "WAFRA" || row.investor === "NAVITAS" ? row.investor : undefined;
}

/**
 * The condition fields for a subject, given the scope it was picked under.
 *
 * One function for both the picked-row and the no-row cases, because they write the SAME
 * five fields and the backend reads them together — a patch that set `subject` but left a
 * stale `facilityCode`, or vice versa, is an alert watching something nobody chose. Kept
 * beside subjectValue so the encode and decode sides cannot drift.
 */
function subjectFields(row: ConcentrationRow | null, s: AlertScope): Partial<AlertConfigInput> {
  // "All investors" is the ABSENCE of the filter, not a value — prc-api turns it into no
  // WHERE clause. Sending it as a literal would file the alert under an investor that does
  // not exist, the same reason PrcFilterBar keeps ALL off the writing surfaces.
  const investor = s.investor === "ALL" ? undefined : s.investor;

  if (!row) {
    return {
      // Explicit even for external, where the backend would default to it anyway: the
      // subject is what picks the condition builder, and leaving it to a fallback is how
      // an internal whole-scope alert would silently get built against the external table
      // and never fire.
      subject: s.scope === "INTERNAL" ? "internal" : "external",
      limitId: undefined,
      limitDesc: undefined,
      jointId: undefined,
      // The facility-level alert: a book with no limit named. Undefined under "All
      // Facilities" so both books are covered, as before.
      facilityCode:
        s.scope === "INTERNAL" ? "INTERNAL" : s.facility === "ALL" ? undefined : s.facility,
      investor,
    };
  }
  if (row.isJoint) {
    // A joint is not a row in the trend table or the concentrations view, so it needs
    // the joint condition builder. Its limitId IS the joint_id the view keys on.
    //
    // No investor: v_prc_joint_utilisation has no investor column, and a joint's investor
    // is its members' (sql/75_) — which joint_id already pins. Sending one would be a field
    // prc-api ignores and the next edit reads back as "All", so the form would appear to
    // lose a setting it never had.
    return {
      subject: "joint",
      jointId: row.limitId,
      limitId: undefined,
      limitDesc: row.limitDesc,
      facilityCode: row.facilityCode,
      investor: undefined,
    };
  }
  return {
    subject: row.facilityCode === "INTERNAL" ? "internal" : "external",
    jointId: undefined,
    limitId: row.limitId,
    // The generated SQL matches on the DESCRIPTION: neither risk_concentrations_trend
    // nor v_prc_concentrations_all carries the synthetic limitId.
    limitDesc: row.limitDesc,
    facilityCode: row.facilityCode,
    // The row's own investor wins over the filter — see pinnedInvestor.
    investor: pinnedInvestor(row) ?? investor,
  };
}

/**
 * The inverse of `subjectFields`: the filter positions an existing alert implies.
 *
 * Everything here comes from the condition SQL prc-api parsed back out, so it can only be
 * as complete as that parse. An alert whose SQL was hand-edited comes back with no
 * structured fields at all and lands on the defaults — which is harmless, because such an
 * alert opens in Advanced with its raw `sql` override, and the override beats every field
 * these filters write.
 */
function scopeFromAlert(alert: AlertConfigInput): AlertScope {
  const code = String(alert.facilityCode ?? "").toUpperCase();
  const isInternal = alert.subject === "internal" || code === "INTERNAL";
  return {
    scope: isInternal ? "INTERNAL" : "EXTERNAL",
    // Only the external books are book-narrowed; INTERNAL is the scope, not a facility. Any facility the alert names is its book.
    facility: code && code !== "INTERNAL" ? code : "ALL",
    // No investor clause means every investor, not the default one — see subjectFields.
    investor:
      alert.investor === "WAFRA" || alert.investor === "NAVITAS" ? alert.investor : "ALL",
  };
}

/**
 * What the alert watches, in a sentence — the same claim prc-api puts in the email body
 * (alerts_config._subject_text).
 *
 * Restated here rather than waiting for the saved description because the scope is now
 * assembled from three controls plus a dropdown, and four controls that each look
 * reasonable can still combine into an alert nobody meant. Reading it back as one sentence
 * is what makes that visible before saving.
 */
function scopeSentence(row: ConcentrationRow | null, s: AlertScope, bookLabel: Record<string, string>): string {
  const investorSuffix = s.investor === "ALL" ? "" : ` (${INVESTOR_LABEL[s.investor]})`;
  if (row) {
    const book = bookLabel[row.facilityCode] ?? row.facilityCode;
    // Read from the same place the payload does, so the sentence cannot claim an investor
    // the condition does not carry — or omit one it does, which is what a picked internal
    // limit under "All investors" would otherwise do.
    const pinned = pinnedInvestor(row);
    const suffix = pinned
      ? ` (${INVESTOR_LABEL[pinned]})`
      : row.isJoint
        ? ""
        : investorSuffix;
    return `${row.isJoint ? "The joint" : "The limit"} "${row.limitDesc}" in ${book}${suffix}.`;
  }
  if (s.scope === "INTERNAL") return `Any internal limit${investorSuffix}.`;
  if (s.facility === "ALL") return `Any limit in either external book${investorSuffix}.`;
  return `Any limit in ${bookLabel[s.facility] ?? s.facility}${investorSuffix}.`;
}

/**
 * Utilisation of a limit's threshold at which a new alert fires, as a percentage.
 *
 * 90 rather than 10 because the field changed direction, not behaviour: it used to ask for the
 * *distance* to the threshold ("within 10%") and now asks where to fire ("at 90% of it"). Both
 * describe the same alert — prc-api converts one into the other — so this default preserves
 * exactly what alerts did before. 90 is also Monitor's amber band, so a new alert agrees with
 * the colour the page already shows.
 */
const DEFAULT_ALERT_AT_PCT = 90;

function freshForm(meta?: AlertsMeta): AlertConfigInput {
  return {
    name: "",
    description: "",
    owners: meta?.defaultOwnerIds ?? [],
    emailSubject: "",
    active: true,
    sql: "",
    alertAtPctOfThreshold: DEFAULT_ALERT_AT_PCT,
    validatorType: "operator",
    // The auto-built condition returns a COUNT of limits at or above the percentage, so
    // "fires when that count is above zero" is the only sensible reading of it. These
    // were previously blank and required the user to work that out; they are still
    // editable under Advanced.
    operator: ">",
    thresholdValue: 0,
    contentType: "Dashboard",
    // Not user-selectable any more. Alert email is delivered by the notification service,
    // which sends text/plain with no attachment, so PNG and CSV have nowhere to go — and
    // superset-docker no longer ships the headless browser a PNG would need. prc-api coerces
    // PNG to TEXT regardless; this keeps the request honest about what is sent.
    reportFormat: "TEXT",
    customWidth: undefined,
    ignoreCache: false,
    // Eastern, not UTC. The people this alert wakes up work to New York hours, and the
    // default view has no timezone picker — so a UTC default meant the un-advanced path
    // silently scheduled 00:00 UTC, which is the previous evening here.
    timezone: "America/New_York",
    logRetention: 90,
    gracePeriod: undefined,
    notificationMethod: "Email",
    recipients: [{ type: "Email", value: "" }],
    ccRecipients: "",
    bccRecipients: "",
    slackChannel: "",
  };
}

function SectionHeader({
  title,
  subtitle,
  required,
  done,
}: {
  title: string;
  subtitle: string;
  required?: boolean;
  done?: boolean;
}) {
  return (
    <div className="flex flex-col items-start gap-0.5 text-left">
      <span className="flex items-center gap-1.5 text-sm font-semibold">
        {title}
        {done ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--approve)]" />
        ) : required ? (
          <span className="text-destructive">*</span>
        ) : null}
      </span>
      <span className="text-xs font-normal text-muted-foreground">{subtitle}</span>
    </div>
  );
}

/** An investor code from the API, as a display name. Falls back to the code so an investor
 *  the UI's vocabulary does not know about is still legible rather than blank. */
function investorName(code?: string | null): string {
  if (!code) return "";
  return INVESTOR_LABEL[code.toUpperCase() as Investor] ?? code;
}

/**
 * Does this configured alert cover what the page is filtered to?
 *
 * "Covers", not "equals" — the filter asks which alerts are watching the thing in front of
 * you, so a BROADER alert matches a narrower filter. A whole-book alert really does watch
 * CAP 1, and an alert with no investor really does watch Navitas', so both stay visible
 * when you narrow to those. Only a genuine mismatch hides a row.
 *
 * An alert with a hand-written condition (`subject: "custom"`) or one whose detail could not
 * be read is ALWAYS shown. Nothing here knows what it watches, and hiding a live alert on a
 * guess is the one outcome worth ruling out.
 */
function alertMatchesScope(a: SupersetAlert, s: AlertScope): boolean {
  if (!a.subject || a.subject === "custom") return true;

  const code = (a.facilityCode ?? "").toUpperCase();
  const isInternal = a.subject === "internal" || code === "INTERNAL";
  if (isInternal !== (s.scope === "INTERNAL")) return false;

  // Facility only means anything on the external books. An alert with no book set covers
  // both, so it is not narrowed out by either.
  if (s.scope === "EXTERNAL" && s.facility !== "ALL" && code && code !== s.facility) return false;

  // Likewise: no investor on the alert means every investor.
  if (s.investor !== "ALL" && a.investor && a.investor.toUpperCase() !== s.investor) return false;

  return true;
}

/** The "Watching" cell: what the alert covers, short enough for a table. */
function alertScopeLabel(a: SupersetAlert, bookLabel: Record<string, string>): string {
  if (a.subject === "custom") return "Custom SQL";
  if (!a.subject) return "—";
  const code = (a.facilityCode ?? "").toUpperCase();
  const where =
    code === "INTERNAL" ? "Internal" : bookLabel[code] ?? (code || "Every external facility");
  const what = a.limitDesc || (a.jointId ? `Joint ${a.jointId}` : "Any limit");
  const who = a.investor ? ` · ${investorName(a.investor)}` : "";
  return `${what} · ${where}${who}`;
}

/**
 * The scope the dialog is working in, stated rather than offered.
 *
 * The three controls live on the PAGE, not in here. That is what lets them filter the
 * configured-alerts table as well as scope a new alert — but it also means the dialog cannot
 * show them, since it is modal over the page that owns them. So it shows the resulting scope
 * as a sentence, and says where it came from, rather than leaving the reader to guess which
 * book they are about to alert on.
 *
 * On an EDIT this is the alert's own scope, recovered from its condition — deliberately NOT
 * the page filters, so opening a CAP 1 alert while the page is filtered to CAP 2 cannot move
 * it on save.
 */
function ScopeLine({ scope, isEditing }: { scope: AlertScope; isEditing: boolean }) {
  const bookLabel = useBookLabels();
  const parts = [
    SCOPE_LABEL[scope.scope],
    scope.scope === "EXTERNAL" && scope.facility !== "ALL"
      ? bookLabel[scope.facility] ?? scope.facility
      : null,
    scope.investor === "ALL" ? "All investors" : INVESTOR_LABEL[scope.investor],
  ].filter(Boolean);
  return (
    <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {parts.map((p) => (
          <Badge key={p as string} variant="secondary" className="font-normal">
            {p}
          </Badge>
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        {isEditing
          ? "This alert's own scope, read back from its condition. Filters on the page do not change it."
          : "From the filters on the Manage Alerts page. Close this to change them."}
      </p>
    </div>
  );
}

/**
 * The page's filter bar: Investor, Facility and Internal/External, in the product's one
 * filter vocabulary.
 *
 * Deliberately the SAME components Monitor, Capacity and Snapshot use rather than three
 * lookalikes — PrcFilterBar exists because the same question used to be asked three
 * different ways, and a fourth spelling on this page would be the next entry in that list.
 * Loans/Leases is the one control not brought across: it narrows an exposure population, and
 * an alert's scope is a set of LIMITS.
 */
function ScopeFilters({
  value,
  onChange,
}: {
  value: AlertScope;
  onChange: (next: AlertScope) => void;
}) {
  const { investorsWithAll } = useInvestorFilterOptions();
  // What the chosen investor has, from the database: its facilities with risk limits drive the Facility list and decide whether External
  // can hold an alert at all.
  const has = useInvestorScopes();
  const investorHas = scopeOfInvestor(has.data, value.investor);
  const facilityOptions = useFacilityOptions(value.investor, true);
  const externalImpossible = value.scope === "EXTERNAL" && !investorHas.isLoading && !investorHas.hasExternal;
  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <ScopeToggle scope={value.scope} onScopeChange={(scope) => onChange({ ...value, scope })} investor={value.investor} />
        <FilterSelect
          label="Investor"
          value={value.investor}
          options={investorsWithAll}
          // the facility a user had picked belongs to the investor they are leaving: back to All
          onChange={(investor) => onChange({ ...value, investor, facility: "ALL" })}
        />
        <FilterSelect
          label="Facility"
          value={value.facility}
          options={facilityOptions}
          onChange={(facility) => onChange({ ...value, facility })}
          wide
        />
      </div>
      {/* Every control stays live in every combination, as everywhere else in the product.
          What changes is that a combination no alert can express says so, rather than one
          being saved quietly that never fires. */}
      {externalImpossible && (
        <p className="text-xs text-destructive">
          {INVESTOR_LABEL[value.investor] ?? value.investor} has no facility with risk limits, so
          there is nothing external to list here, and a new alert on this combination could
          never fire. Switch to Internal, or to All investors.
        </p>
      )}
      {value.scope === "INTERNAL" && value.facility !== "ALL" && (
        <p className="text-xs text-muted-foreground">
          Internal limits are not held per book, so Facility does not narrow this alert.
        </p>
      )}
    </div>
  );
}

/** The "Any limit" option's label. It has to name the scope: under the filters that option
 *  is no longer one alert but four, and "Any limit — whole book" described only one. */
function anyLimitLabel(s: AlertScope, bookLabel: Record<string, string>): string {
  if (s.scope === "INTERNAL") return "Any internal limit";
  if (s.facility === "ALL") return "Any limit — every external facility";
  return `Any limit — ${bookLabel[s.facility] ?? s.facility}`;
}

/** A subject the loaded rows do not contain — see `unresolvedLabel` in the page below. */
const UNRESOLVED = "__unresolved__";

/**
 * "Alert me about": the whole scope, or one limit or joint inside it.
 *
 * One component for both views. It was the same forty lines twice — once in the simple view
 * and once in the Alert condition accordion — and the two had already drifted in their
 * helper text.
 */
function SubjectSelect({
  rows,
  selectedRow,
  alertScope,
  unresolvedLabel,
  onPick,
}: {
  rows: ConcentrationRow[];
  selectedRow: ConcentrationRow | null;
  alertScope: AlertScope;
  /** Set when the form names a limit that is not in `rows`; null otherwise. */
  unresolvedLabel: string | null;
  onPick: (row: ConcentrationRow | null) => void;
}) {
  const bookLabel = useBookLabels();
  const singleLimits = rows.filter((r) => !r.isJoint);
  const jointLimits = rows.filter((r) => r.isJoint);
  const value = selectedRow
    ? subjectValue(selectedRow)
    : unresolvedLabel
      ? UNRESOLVED
      : ANY_LIMIT;

  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v === UNRESOLVED) return;
        onPick(v === ANY_LIMIT ? null : rows.find((r) => subjectValue(r) === v) ?? null);
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder="Select a limit or joint" />
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        <SelectItem value={ANY_LIMIT}>{anyLimitLabel(alertScope, bookLabel)}</SelectItem>
        {/* The alert's real subject, kept selectable-looking while its row is still
            loading — or missing, if the limit was renamed or the filters moved off it.
            Without this the trigger falls back to "Any limit", and saving from there
            silently widens a per-limit alert to the whole scope. */}
        {unresolvedLabel && <SelectItem value={UNRESOLVED}>{unresolvedLabel}</SelectItem>}
        {singleLimits.map((r) => (
          <SelectItem key={subjectValue(r)} value={subjectValue(r)}>
            {r.limitDesc} · {bookLabel[r.facilityCode] ?? r.facilityCode} · threshold{" "}
            {thr(r.thresholdPct)}
          </SelectItem>
        ))}
        {jointLimits.length > 0 && (
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
            Joints
          </div>
        )}
        {jointLimits.map((r) => (
          <SelectItem key={subjectValue(r)} value={subjectValue(r)}>
            {r.limitDesc} · {bookLabel[r.facilityCode] ?? r.facilityCode} · utilisation basis
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Configure Superset PRC alerts from Athena — mirrors the Superset "Add Alert" modal
 *  field-for-field. Talks to the PRC backend, which proxies the Superset /api/v1/report/ API. */
export default function AlertsConfigPage() {
  const bookLabel = useBookLabels();
  const investorScopes = useInvestorScopes();
  const { data: alerts = [], isLoading } = useAlertsConfig();
  const { data: meta } = useAlertsMeta();
  const create = useCreateAlert();
  const update = useUpdateAlert();
  const setActive = useSetAlertActive();
  const remove = useDeleteAlert();

  const [open, setOpen] = useState(false);
  // null = the dialog is creating; a number = it is editing that alert. One piece of
  // state drives the title, the submit button, which mutation runs and whether the form
  // is seeded fresh or loaded.
  const [editingId, setEditingId] = useState<number | null>(null);
  const isEditing = editingId !== null;
  const { data: detail, isLoading: detailLoading } = useAlert(open ? editingId : null);
  const [form, setForm] = useState<AlertConfigInput>(() => freshForm());
  const [sched, setSched] = useState<ScheduleState>(DEFAULT_SCHEDULE);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  // Off by default: the simple view is the one a risk or business reader can complete
  // without knowing what a Superset database connection is.
  const [advanced, setAdvanced] = useState(false);
  const set = (patch: Partial<AlertConfigInput>) => setForm((f) => ({ ...f, ...patch }));

  const { data: eligible = [], isLoading: recipientsLoading } = useAlertRecipients();

  // Recipients live on the form as a comma-joined string (Superset's shape). The simple
  // view edits them as a set; deriving rather than storing a second copy keeps the two
  // views in sync when you flip the toggle mid-edit.
  const chosenRecipients = (form.recipients?.[0]?.value ?? "")
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const toggleRecipient = (email: string, checked: boolean) => {
    const next = new Set(chosenRecipients);
    if (checked) next.add(email);
    else next.delete(email);
    set({ recipients: [{ type: "Email", value: [...next].join(", ") }] });
  };

  /**
   * The page's filters. They do two jobs, which is why they sit out here rather than in the
   * dialog: they narrow the configured-alerts table below, and they are the scope a NEW
   * alert is authored under.
   */
  const [pageScope, setPageScope] = useState<AlertScope>(DEFAULT_SCOPE);

  /**
   * The scope of the alert currently in the dialog — the page filters for a new one, the
   * alert's OWN scope for an existing one.
   *
   * Separate state, because the two must not be the same thing. An existing alert's scope is
   * a property of that alert; if it tracked the filters, opening an alert while the page was
   * filtered to CAP 2 and saving would move a CAP 1 alert to CAP 2 without anyone asking.
   */
  const [dialogScope, setDialogScope] = useState<AlertScope>(DEFAULT_SCOPE);

  // Which scope the limit options are fetched for. The open dialog wins, so an alert on a
  // book the page is not currently filtered to still resolves its own subject.
  const rowScope = open ? dialogScope : pageScope;

  // The "Alert on" options are exactly the rows Monitor shows for the SAME filters, joints
  // included. Fetched here rather than from a bespoke endpoint so a limit can never appear
  // on Monitor and be un-alertable, or vice versa — which now extends to the filters: a
  // limit is offered here exactly when Monitor would list it under the same three controls.
  //
  // One request rather than the previous two. Both scopes used to be fetched and merged,
  // which is also why Navitas' internal limits were absent from this dropdown entirely:
  // the internal call took the default investor, so only Wafra's appetite was alertable.
  const today = format(new Date(), "yyyy-MM-dd");
  const { data: monitorData } = useConcentration(
    concentrationFacilityParam(rowScope.scope, rowScope.facility),
    today,
    true,
    "approved",
    rowScope.investor,
    rowScope.facility,
  );
  const monitorRows = useMemo(() => monitorData?.rows ?? [], [monitorData]);

  // Which row the form currently points at, derived rather than stored — one copy of the
  // truth.
  //
  // The limitDesc fallback is what makes EDITING a per-limit alert work. prc-api recovers
  // the condition by parsing its SQL, and that SQL matches on the DESCRIPTION — neither
  // risk_concentrations_trend nor v_prc_concentrations_all carries the synthetic limitId —
  // so a loaded alert has a limitDesc and no limitId. Matching on limitId alone left every
  // edited per-limit alert showing "Any limit", and saving it widened the alert to the
  // whole book without saying so.
  const selectedRow = useMemo(() => {
    if (form.jointId) return monitorRows.find((r) => r.isJoint && r.limitId === form.jointId) ?? null;
    if (form.limitId) return monitorRows.find((r) => !r.isJoint && r.limitId === form.limitId) ?? null;
    if (form.limitDesc) {
      const desc = form.limitDesc.trim().toLowerCase();
      return (
        monitorRows.find(
          (r) =>
            !r.isJoint &&
            r.limitDesc.trim().toLowerCase() === desc &&
            (!form.facilityCode || r.facilityCode === form.facilityCode),
        ) ?? null
      );
    }
    return null;
  }, [monitorRows, form.jointId, form.limitId, form.limitDesc, form.facilityCode]);

  // The form names a subject, but no loaded row is it — because the rows are still in
  // flight, or because the limit has been renamed or removed. Either way the dropdown must
  // not fall back to "Any limit"; see SubjectSelect.
  const unresolvedSubject = form.limitDesc || form.jointId || "";
  const unresolvedLabel =
    !selectedRow && unresolvedSubject
      ? `${unresolvedSubject} (not in the current selection)`
      : null;

  // The alerts this page's filters are asking about. An alert BROADER than the filter still
  // matches — a whole-book alert does cover CAP 1 — and one nothing could classify always
  // does; see alertMatchesScope.
  const visibleAlerts = useMemo(
    () => alerts.filter((a) => alertMatchesScope(a, pageScope)),
    [alerts, pageScope],
  );
  const hiddenCount = alerts.length - visibleAlerts.length;

  // Start each "Add Alert" fresh, defaulting owners to the current Superset user. An EDIT
  // is not seeded here — it waits for the detail fetch below, so the form is never briefly
  // populated with defaults that would be mistaken for the alert's real values.
  useEffect(() => {
    if (!open) return;
    setShowCc(false);
    setShowBcc(false);
    if (!isEditing) {
      setForm(freshForm(meta));
      setSched(DEFAULT_SCHEDULE);
      // External / all facilities / all investors — the scope every alert had before the
      // filters existed, so "New alert" still starts on the whole external book.
      setDialogScope(pageScope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId]);

  // The alert being edited, once it arrives.
  useEffect(() => {
    if (!open || !isEditing || !detail) return;
    // `raw` and `id` are transport, not form fields — sending them back would put unknown
    // keys into the payload builder.
    const { id: _id, raw: _raw, ...body } = detail;
    setForm(body);
    setSched(parseCron(detail.crontab));
    // The filters follow the alert, never the other way round. They are derived from the
    // condition prc-api parsed back out rather than stored separately, so opening an alert
    // and saving it unchanged cannot move its scope — which is the same property the
    // condition round-trip itself is built for.
    setDialogScope(scopeFromAlert(detail));
    setShowCc(!!detail.ccRecipients);
    setShowBcc(!!detail.bccRecipients);
    // A condition the backend could not parse comes back as a raw `sql` override, and that
    // override beats every structured field on save. Forcing Advanced open is what stops
    // someone changing "alert at %" in the simple view and watching it do nothing.
    if (detail.sql) setAdvanced(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId, detail]);

  // If meta arrives after the dialog is already open, seed the default owner then. Create
  // only: an edit's owners come from the alert itself, and an alert with no owners should
  // stay that way rather than silently gaining the current user on the next save.
  useEffect(() => {
    if (open && !isEditing && meta && (form.owners?.length ?? 0) === 0) {
      setForm((f) => ({ ...f, owners: meta.defaultOwnerIds }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, meta]);

  function openCreate() {
    setEditingId(null);
    setOpen(true);
  }

  function openEdit(id: number) {
    setEditingId(id);
    setOpen(true);
  }

  // The simple view never shows the Contents section, but submit() still requires a
  // dashboard — Superset attaches the screenshot to one. Pick the PRC dashboard by
  // name, falling back to whatever exists, so the field is satisfied without ever
  // asking. Only fills a blank, so an advanced user's explicit pick is never overwritten.
  useEffect(() => {
    if (!open || !meta?.dashboards?.length || form.dashboardId) return;
    const prc =
      meta.dashboards.find((d) => /portfolio risk concentration/i.test(d.title)) ??
      meta.dashboards[0];
    if (prc) setForm((f) => ({ ...f, dashboardId: prc.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meta]);

  const selectedOwners = (form.owners ?? [])
    .map((id) => meta?.owners.find((o) => o.id === id))
    .filter((o): o is { id: number; name: string } => !!o);
  const availableOwners = (meta?.owners ?? []).filter((o) => !(form.owners ?? []).includes(o.id));

  function submit() {
    if (!form.name?.trim()) return toast.error("Give the alert a name.");
    // operator/thresholdValue now default to "> 0" in freshForm, so these only fire if
    // someone clears them under Advanced.
    if (!form.operator) return toast.error("Select a condition for the alert.");
    if (form.thresholdValue === undefined || Number.isNaN(form.thresholdValue))
      return toast.error("A condition value is required.");
    // Required only when no raw SQL override is supplied — Advanced can replace the whole
    // condition, in which case this field is not used to build anything.
    if (!form.sql?.trim()) {
      const at = form.alertAtPctOfThreshold;
      if (at === undefined || Number.isNaN(at))
        return toast.error("Say at what % of its threshold the alert should fire.");
      // The field had no bounds at all before: negative, 0 and >100 all saved, and each one
      // produces an alert that either fires on every snapshot or can never fire. Both are
      // silent failures — Superset accepts the condition either way.
      if (at <= 0 || at > 100)
        return toast.error("The alert percentage must be above 0 and no more than 100.");
    }
    // Only reachable from the advanced view — the simple view defaults dashboardId.
    if (form.contentType === "Dashboard" && !form.dashboardId)
      return toast.error("Select a dashboard.");
    if (form.contentType === "Chart" && !form.chartId) return toast.error("Select a chart.");
    if (form.notificationMethod === "Email" && !form.recipients?.[0]?.value?.trim())
      return toast.error("Choose at least one person to email.");
    // prc-api rejects this too — it is the one filter combination that would save cleanly
    // and then never fire — but catching it here names the two controls to change instead
    // of surfacing a 400 after the round trip.
    const dialogHas = scopeOfInvestor(investorScopes.data, dialogScope.investor);
    if (dialogScope.scope === "EXTERNAL" && !dialogHas.isLoading && !dialogHas.hasExternal)
      return toast.error(
        `${INVESTOR_LABEL[dialogScope.investor] ?? dialogScope.investor} has no facility with risk limits, so this alert could never fire. Switch to Internal, or to All investors.`,
      );

    const body: AlertConfigInput = {
      ...form,
      // Rebuilt from the filters and the picked row rather than trusted from the form. The
      // two are kept in step by every handler that touches either, and this is the one line
      // that makes that guarantee rather than assuming it.
      //
      // Except when the subject is unresolved — the rows are still in flight, or the limit
      // has been renamed. `selectedRow` is null there for a reason that is not "the user
      // chose the whole book", so rebuilding would quietly widen a per-limit alert to its
      // whole scope. The alert's own condition fields are left exactly as they loaded.
      ...(unresolvedLabel ? {} : subjectFields(selectedRow, dialogScope)),
      crontab: buildCron(sched),
      databaseName: form.databaseId ? undefined : meta?.defaultDatabaseName || "PostgreSQL",
    };
    if (editingId !== null) {
      update.mutate(
        { id: editingId, body },
        {
          onSuccess: () => {
            toast.success("Alert updated in Superset.");
            setOpen(false);
          },
          onError: (e: any) =>
            toast.error(e?.response?.data?.detail ?? "Failed to update alert."),
        },
      );
      return;
    }
    create.mutate(body, {
      onSuccess: () => {
        toast.success("Alert created in Superset.");
        setOpen(false);
      },
      onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Failed to create alert."),
    });
  }

  function toggleActive(id: number, active: boolean) {
    setActive.mutate(
      { id, active },
      {
        onSuccess: () => toast.success(active ? "Alert enabled." : "Alert disabled."),
        onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Failed to update alert."),
      },
    );
  }

  function del(id: number) {
    remove.mutate(id, {
      onSuccess: () => toast.success("Alert deleted."),
      onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Failed to delete alert."),
    });
  }

  const fieldLabel = "text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground";

  return (
    // Alerts hangs off Monitor in the step navigation, so that is where back goes. No
    // `?from=` handling here, unlike Manage Limits: nothing links to this page from anywhere
    // else, so a lookup table would be branches that can never be taken.
    <ManageLimitsShell backTo="/risk-concentrations" backLabel="← Back to Monitor">
      <PageHeader
        title="Manage Alerts"
        right={
          // Controlled, because the dialog now has two entry points: this trigger and the
          // per-row edit action. onOpenChange resets editingId on CLOSE rather than on open,
          // so a re-open from the trigger cannot inherit the last edited id.
          <Dialog
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) setEditingId(null);
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={openCreate} size="sm">
                <Plus className="mr-1 h-4 w-4" /> New alert
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto p-0 sm:max-w-[520px]">
              <DialogHeader className="border-b border-border px-6 py-4">
                <DialogTitle>{isEditing ? "Edit alert" : "Add Alert"}</DialogTitle>
              </DialogHeader>

              {/* ── Simple view ──────────────────────────────────────────────
                  The full Superset field set is ~20 inputs across five sections,
                  most of which ask a question a risk or business reader has no basis
                  to answer — which Superset database, PNG or CSV, screenshot width,
                  log retention. They are not removed, just defaulted and moved behind
                  the toggle at the bottom, so a technical user can still tune an
                  alert here rather than in Superset. */}
              {!advanced && (
                <div className="space-y-5 px-6 py-5">
                  <div className="space-y-1.5">
                    <Label className={fieldLabel}>Name this alert *</Label>
                    <Input
                      value={form.name ?? ""}
                      onChange={(e) => set({ name: e.target.value })}
                      placeholder="e.g. CA concentration nearing its limit"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className={fieldLabel}>Alert me about *</Label>
                    <ScopeLine scope={dialogScope} isEditing={isEditing} />
                    <SubjectSelect
                      rows={monitorRows}
                      selectedRow={selectedRow}
                      alertScope={dialogScope}
                      unresolvedLabel={unresolvedLabel}
                      onPick={(row) => set(subjectFields(row, dialogScope))}
                    />
                    <p className="text-xs text-muted-foreground">
                      {scopeSentence(selectedRow, dialogScope, bookLabel)}
                      {selectedRow
                        ? selectedRow.isJoint
                          ? " A joint is judged on its lowest member threshold, so it has no threshold of its own to set here."
                          : ` Threshold ${thr(selectedRow.thresholdPct)}, currently at ${pct(selectedRow.currentDollarPct)}.`
                        : " Fires when any of them gets close to its threshold."}
                    </p>
                    {unresolvedLabel && (
                      <p className="text-xs text-amber-500">
                        This alert names a limit that is not in the list above. It is left
                        untouched on save — pick a different subject to replace it.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className={fieldLabel}>Send the alert at *</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        className="w-24"
                        value={form.alertAtPctOfThreshold ?? DEFAULT_ALERT_AT_PCT}
                        onChange={(e) =>
                          set({
                            alertAtPctOfThreshold:
                              e.target.value === "" ? undefined : Number(e.target.value),
                          })
                        }
                      />
                      <span className="text-sm text-muted-foreground">
                        % of its threshold
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      90% is the amber band on Monitor. 95% is red-only — fewer, later warnings.
                      100% fires only on an actual breach.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className={fieldLabel}>Check *</Label>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Select
                        value={sched.unit}
                        onValueChange={(v) => setSched((s) => ({ ...s, unit: v as ScheduleUnit }))}
                      >
                        <SelectTrigger className="w-[130px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SCHEDULE_UNITS.map((u) => (
                            <SelectItem key={u} value={u}>
                              {SCHEDULE_LABEL[u]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {sched.unit === "week" && (
                        <Select
                          value={String(sched.weekday)}
                          onValueChange={(v) => setSched((s) => ({ ...s, weekday: Number(v) }))}
                        >
                          <SelectTrigger className="w-[130px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {WEEKDAYS.map((d) => (
                              <SelectItem key={d.value} value={String(d.value)}>
                                {d.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {sched.unit === "month" && (
                        <>
                          <span>on day</span>
                          <Input
                            type="number"
                            min={1}
                            max={31}
                            className="w-16"
                            value={sched.monthday}
                            onChange={(e) =>
                              setSched((s) => ({ ...s, monthday: Number(e.target.value) }))
                            }
                          />
                        </>
                      )}
                      {sched.unit !== "hour" && (
                        <>
                          <span>at</span>
                          <Input
                            type="number"
                            min={0}
                            max={23}
                            className="w-16"
                            value={sched.hour}
                            onChange={(e) =>
                              setSched((s) => ({ ...s, hour: Number(e.target.value) }))
                            }
                          />
                          <span>:</span>
                          <Input
                            type="number"
                            min={0}
                            max={59}
                            className="w-16"
                            value={sched.minute}
                            onChange={(e) =>
                              setSched((s) => ({ ...s, minute: Number(e.target.value) }))
                            }
                          />
                          {/* Reads the form rather than a literal. This used to say "UTC"
                              unconditionally, so an alert set up under Advanced in Eastern
                              still claimed UTC here. */}
                          <span className="text-muted-foreground">
                            {TZ_SHORT[form.timezone ?? "UTC"] ?? form.timezone}
                          </span>
                        </>
                      )}
                      {sched.unit === "hour" && (
                        <span className="text-muted-foreground">on the hour</span>
                      )}
                    </div>
                  </div>

                  {/* Checkboxes rather than a textarea: the gateway only lets an alert
                      reach people with an active subscription to this product, and it
                      used to drop anything else on save without saying so. */}
                  <div className="space-y-1.5">
                    <Label className={fieldLabel}>Email to *</Label>
                    {recipientsLoading && (
                      <p className="text-sm text-muted-foreground">Loading people…</p>
                    )}
                    {!recipientsLoading && eligible.length === 0 && (
                      <p className="text-sm text-destructive">
                        Nobody currently has access to this product, so an alert has no one
                        to notify. Add a subscription first.
                      </p>
                    )}
                    <div className="space-y-1">
                      {eligible.map((email) => (
                        <label
                          key={email}
                          className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted/40"
                        >
                          <Checkbox
                            checked={chosenRecipients.includes(email)}
                            onCheckedChange={(c) => toggleRecipient(email, !!c)}
                          />
                          <span>{email}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only people with access to Portfolio Concentration &amp; Exposure
                      Intelligence can receive its alerts.
                    </p>
                  </div>
                </div>
              )}

              {advanced && (
              <Accordion
                type="multiple"
                defaultValue={["general", "condition", "contents", "schedule", "notification"]}
                className="px-6"
              >
                {/* ── General information ── */}
                <AccordionItem value="general">
                  <AccordionTrigger className="hover:no-underline">
                    <SectionHeader
                      title="General information"
                      subtitle="Set up basic details, such as name and description."
                      required
                    />
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Alert name *</Label>
                      <Input
                        value={form.name ?? ""}
                        onChange={(e) => set({ name: e.target.value })}
                        placeholder="Enter alert name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Owners *</Label>
                      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input p-1.5">
                        {selectedOwners.map((o) => (
                          <Badge key={o.id} variant="secondary" className="gap-1">
                            {o.name}
                            <button
                              type="button"
                              onClick={() =>
                                set({ owners: (form.owners ?? []).filter((id) => id !== o.id) })
                              }
                              className="ml-0.5 rounded-full hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                        {availableOwners.length > 0 && (
                          <Select
                            value=""
                            onValueChange={(v) =>
                              set({ owners: [...(form.owners ?? []), Number(v)] })
                            }
                          >
                            <SelectTrigger className="h-7 w-auto border-0 px-2 text-xs shadow-none focus:ring-0">
                              <SelectValue placeholder={selectedOwners.length ? "Add owner…" : "Select owners"} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableOwners.map((o) => (
                                <SelectItem key={o.id} value={String(o.id)}>
                                  {o.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Description</Label>
                      <Input
                        value={form.description ?? ""}
                        onChange={(e) => set({ description: e.target.value })}
                        placeholder="Include description to be sent with alert"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={form.active ?? true} onCheckedChange={(v) => set({ active: v })} />
                      <Label>Alert is active</Label>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ── Alert condition ── */}
                <AccordionItem value="condition">
                  <AccordionTrigger className="hover:no-underline">
                    <SectionHeader
                      title="Alert condition"
                      subtitle="Pick what to watch and how close to its threshold it has to get."
                      required
                    />
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    {/* ── What to watch ──────────────────────────────────────────
                        Was a SQL textarea plus a bare operator and number, which asked
                        the author to know the schema and left nothing connecting an
                        alert to the limit it is about. The options here ARE the Monitor
                        rows, so the two screens cannot drift apart. */}
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Alert on *</Label>
                      <ScopeLine scope={dialogScope} isEditing={isEditing} />
                      <SubjectSelect
                        rows={monitorRows}
                        selectedRow={selectedRow}
                        alertScope={dialogScope}
                        unresolvedLabel={unresolvedLabel}
                        onPick={(row) => set(subjectFields(row, dialogScope))}
                      />
                      <p className="text-xs text-muted-foreground">
                        {scopeSentence(selectedRow, dialogScope, bookLabel)}
                        {selectedRow
                          ? selectedRow.isJoint
                            ? " A joint is judged on its lowest member threshold, so it has no threshold of its own."
                            : ` Threshold ${thr(selectedRow.thresholdPct)}, currently at ${pct(selectedRow.currentDollarPct)}.`
                          : " Fires when any of them reaches the percentage below."}
                      </p>
                      {unresolvedLabel && (
                        <p className="text-xs text-amber-500">
                          This alert names a limit that is not in the current selection. It is
                          left untouched on save.
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className={`${fieldLabel} flex items-center gap-1`}>
                        Fire when utilisation of the threshold is at or above
                        <Info
                          className="h-3 w-3"
                          aria-label="90% matches the in-app amber band; 95% is red-only; 100% is breach-only."
                        />
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          className="w-28"
                          value={form.alertAtPctOfThreshold ?? DEFAULT_ALERT_AT_PCT}
                          onChange={(e) =>
                            set({
                              alertAtPctOfThreshold:
                                e.target.value === "" ? undefined : Number(e.target.value),
                            })
                          }
                        />
                        <span className="text-xs text-muted-foreground">
                          % — 90 matches the amber band on Monitor, 95 is red-only, 100 is
                          breach-only.
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Database *</Label>
                      <Select
                        value={form.databaseId ? String(form.databaseId) : ""}
                        onValueChange={(v) => set({ databaseId: Number(v) })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select database" />
                        </SelectTrigger>
                        <SelectContent>
                          {meta?.databases.map((d) => (
                            <SelectItem key={d.id} value={String(d.id)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* ── Advanced ────────────────────────────────────────────────
                        Kept, not removed. Alerts authored before the dropdown existed
                        carry hand-written SQL, and deleting these fields would make
                        those uneditable from Athena. */}
                    <details className="rounded-md border border-border/60">
                      <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground hover:bg-muted/30">
                        Advanced — custom SQL, operator and value
                      </summary>
                      <div className="space-y-4 border-t border-border/60 p-3">
                        <div className="space-y-1.5">
                          <Label className={`${fieldLabel} flex items-center gap-1`}>
                            SQL Query
                            <Info
                              className="h-3 w-3"
                              aria-label="Return a single numeric value. The alert fires when that value meets the condition below."
                            />
                          </Label>
                          <Textarea
                            rows={5}
                            className="font-mono text-xs leading-5"
                            value={form.sql ?? ""}
                            onChange={(e) => set({ sql: e.target.value })}
                            placeholder="Leave blank to auto-build the breach query for the selection above."
                          />
                          <p className="text-xs text-muted-foreground">
                            Anything here overrides the selection above entirely.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className={fieldLabel}>Trigger Alert If...</Label>
                            <Select
                              value={form.operator ?? ""}
                              onValueChange={(v) =>
                                set({ operator: v as AlertConfigInput["operator"] })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Condition" />
                              </SelectTrigger>
                              <SelectContent>
                                {OPERATORS.map((o) => (
                                  <SelectItem key={o} value={o}>
                                    {o}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className={fieldLabel}>Value</Label>
                            <Input
                              type="number"
                              placeholder="Value"
                              value={form.thresholdValue ?? ""}
                              onChange={(e) =>
                                set({
                                  thresholdValue:
                                    e.target.value === "" ? undefined : Number(e.target.value),
                                })
                              }
                            />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          The generated query returns a count of limits at or within the
                          distance, so the default <code>&gt; 0</code> means "fire if any
                          of them are".
                        </p>
                      </div>
                    </details>
                  </AccordionContent>
                </AccordionItem>

                {/* ── Alert contents ── */}
                <AccordionItem value="contents">
                  <AccordionTrigger className="hover:no-underline">
                    <SectionHeader
                      title="Alert contents"
                      subtitle="Customize data source, filters, and layout."
                      required
                    />
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Content type *</Label>
                      <Select
                        value={form.contentType}
                        onValueChange={(v) => set({ contentType: v as "Dashboard" | "Chart" })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Dashboard">Dashboard</SelectItem>
                          <SelectItem value="Chart">Chart</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.contentType === "Chart" ? (
                      <div className="space-y-1.5">
                        <Label className={fieldLabel}>Select chart *</Label>
                        <Select
                          value={form.chartId ? String(form.chartId) : ""}
                          onValueChange={(v) => set({ chartId: Number(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select chart to use" />
                          </SelectTrigger>
                          <SelectContent>
                            {meta?.charts.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label className={fieldLabel}>Select dashboard *</Label>
                        <Select
                          value={form.dashboardId ? String(form.dashboardId) : ""}
                          onValueChange={(v) => set({ dashboardId: Number(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select dashboard to use" />
                          </SelectTrigger>
                          <SelectContent>
                            {meta?.dashboards.map((d) => (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {/* The "Content format" selector (PNG / CSV / text) used to sit here. It
                        was removed rather than trimmed to one option: alert email now goes
                        through the notification service, which sends text/plain and cannot
                        carry an attachment, so TEXT is the only deliverable format and there
                        is nothing left to choose. reportFormat is pinned to "TEXT" in the form
                        defaults. */}
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Screenshot width</Label>
                      <Input
                        type="number"
                        placeholder="Input custom width in pixels"
                        value={form.customWidth ?? ""}
                        onChange={(e) =>
                          set({ customWidth: e.target.value === "" ? undefined : Number(e.target.value) })
                        }
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={form.ignoreCache ?? false}
                        onCheckedChange={(v) => set({ ignoreCache: v === true })}
                      />
                      Ignore cache when generating report
                    </label>
                  </AccordionContent>
                </AccordionItem>

                {/* ── Schedule ── */}
                <AccordionItem value="schedule">
                  <AccordionTrigger className="hover:no-underline">
                    <SectionHeader
                      title="Schedule"
                      subtitle="Define delivery schedule, timezone, and frequency settings."
                      done
                    />
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Schedule type *</Label>
                      <Select value="recurring" onValueChange={() => undefined}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="recurring">Recurring (every)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Schedule *</Label>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span>Every</span>
                        <Select
                          value={sched.unit}
                          onValueChange={(v) => setSched((s) => ({ ...s, unit: v as ScheduleUnit }))}
                        >
                          <SelectTrigger className="w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SCHEDULE_UNITS.map((u) => (
                              <SelectItem key={u} value={u}>
                                {u}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {sched.unit === "week" && (
                          <>
                            <span>on</span>
                            <Select
                              value={String(sched.weekday)}
                              onValueChange={(v) => setSched((s) => ({ ...s, weekday: Number(v) }))}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {WEEKDAYS.map((d) => (
                                  <SelectItem key={d.value} value={String(d.value)}>
                                    {d.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </>
                        )}
                        {sched.unit === "month" && (
                          <>
                            <span>on day</span>
                            <Input
                              type="number"
                              min={1}
                              max={31}
                              className="w-16"
                              value={sched.monthday}
                              onChange={(e) =>
                                setSched((s) => ({ ...s, monthday: Number(e.target.value) }))
                              }
                            />
                          </>
                        )}
                        {sched.unit !== "hour" && (
                          <>
                            <span>at</span>
                            <Input
                              type="number"
                              min={0}
                              max={23}
                              className="w-16"
                              value={sched.hour}
                              onChange={(e) => setSched((s) => ({ ...s, hour: Number(e.target.value) }))}
                            />
                            <span>:</span>
                          </>
                        )}
                        <Input
                          type="number"
                          min={0}
                          max={59}
                          className="w-16"
                          value={sched.minute}
                          onChange={(e) => setSched((s) => ({ ...s, minute: Number(e.target.value) }))}
                        />
                        {sched.unit === "hour" && <span className="text-muted-foreground">min past the hour</span>}
                      </div>
                      <p className="text-[11px] text-muted-foreground">cron: {buildCron(sched)}</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Timezone *</Label>
                      <Select value={form.timezone} onValueChange={(v) => set({ timezone: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Log retention *</Label>
                      <Select
                        value={String(form.logRetention ?? 90)}
                        onValueChange={(v) => set({ logRetention: Number(v) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LOG_RETENTION_DAYS.map((d) => (
                            <SelectItem key={d} value={String(d)}>
                              {d} days
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Grace period</Label>
                      <Input
                        type="number"
                        placeholder="Time in seconds"
                        value={form.gracePeriod ?? ""}
                        onChange={(e) =>
                          set({ gracePeriod: e.target.value === "" ? undefined : Number(e.target.value) })
                        }
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ── Notification method ── */}
                <AccordionItem value="notification" className="border-b-0">
                  <AccordionTrigger className="hover:no-underline">
                    <SectionHeader
                      title="Notification method"
                      subtitle="Choose notification method and recipients."
                      required
                    />
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className={fieldLabel}>Notification method</Label>
                      <Select
                        value={form.notificationMethod}
                        onValueChange={(v) => set({ notificationMethod: v as "Email" | "Slack" })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="Slack">Slack</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.notificationMethod === "Slack" ? (
                      <div className="space-y-1.5">
                        <Label className={fieldLabel}>Slack channel</Label>
                        <Input
                          value={form.slackChannel ?? ""}
                          onChange={(e) => set({ slackChannel: e.target.value })}
                          placeholder="#risk-alerts"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="space-y-1.5">
                          <Label className={fieldLabel}>Email subject name (optional)</Label>
                          <Input
                            value={form.emailSubject ?? ""}
                            onChange={(e) => set({ emailSubject: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className={fieldLabel}>Email recipients *</Label>
                          <Textarea
                            rows={2}
                            value={form.recipients?.[0]?.value ?? ""}
                            onChange={(e) => set({ recipients: [{ type: "Email", value: e.target.value }] })}
                          />
                          <p className="text-[11px] text-muted-foreground">
                            Recipients are separated by "," or ";"
                          </p>
                        </div>
                        {showCc ? (
                          <div className="space-y-1.5">
                            <Label className={fieldLabel}>CC recipients</Label>
                            <Textarea
                              rows={2}
                              value={form.ccRecipients ?? ""}
                              onChange={(e) => set({ ccRecipients: e.target.value })}
                            />
                          </div>
                        ) : null}
                        {showBcc ? (
                          <div className="space-y-1.5">
                            <Label className={fieldLabel}>BCC recipients</Label>
                            <Textarea
                              rows={2}
                              value={form.bccRecipients ?? ""}
                              onChange={(e) => set({ bccRecipients: e.target.value })}
                            />
                          </div>
                        ) : null}
                        <div className="flex gap-5">
                          {!showCc && (
                            <button
                              type="button"
                              onClick={() => setShowCc(true)}
                              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                            >
                              <Mail className="h-3.5 w-3.5" /> Add CC Recipients
                            </button>
                          )}
                          {!showBcc && (
                            <button
                              type="button"
                              onClick={() => setShowBcc(true)}
                              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                            >
                              <Mail className="h-3.5 w-3.5" /> Add BCC Recipients
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              )}

              <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium">Advanced settings</p>
                  <p className="text-[11px] text-muted-foreground">
                    {advanced
                      ? "Every Superset field. Anything left alone keeps the default."
                      : "Owners, database, report format, custom SQL, retention."}
                  </p>
                </div>
                <Switch checked={advanced} onCheckedChange={setAdvanced} />
              </div>

              <DialogFooter className="border-t border-border px-6 py-4">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={create.isPending || update.isPending || (isEditing && detailLoading)}
                >
                  {isEditing
                    ? update.isPending
                      ? "Saving…"
                      : "Save changes"
                    : create.isPending
                      ? "Adding…"
                      : "Add"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* The filters sit on the PAGE, above the table they narrow — the same place and the
          same order as on Monitor, Capacity and Snapshot. They also supply the scope a new
          alert is created with, which is why "New alert" reads as "alert me about what I am
          currently looking at". */}
      <div className="mt-6">
        <ScopeFilters value={pageScope} onChange={setPageScope} />
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BellRing className="h-4 w-4" /> Configured Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading alerts…</p>
          ) : alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No alerts configured yet.</p>
          ) : visibleAlerts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              None of the {alerts.length} configured alert{alerts.length === 1 ? "" : "s"}{" "}
              watches this selection. Widen the filters above to see them.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Watching</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleAlerts.map((a) => (
                  // The whole row opens the alert. The controls inside it must therefore
                  // stop propagation, or toggling Active or hitting Delete would also open
                  // the editor over the top of what you just did.
                  <TableRow
                    key={a.id}
                    onClick={() => openEdit(a.id)}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <div className="font-medium">{a.name}</div>
                      {a.description && (
                        <div className="text-[11px] text-muted-foreground">{a.description}</div>
                      )}
                    </TableCell>
                    {/* What the alert covers, so the filter above is checkable rather than
                        something the reader has to take on trust. */}
                    <TableCell className="text-xs text-muted-foreground">
                      {alertScopeLabel(a, bookLabel)}
                    </TableCell>
                    <TableCell className="tabular-nums text-xs">{a.crontab ?? "—"}</TableCell>
                    <TableCell>
                      <div
                        className="flex items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={a.active ?? false}
                          onCheckedChange={(v) => toggleActive(a.id, v)}
                        />
                        <Badge variant={a.active ? "default" : "secondary"}>
                          {a.active ? "Active" : "Paused"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Both affordances, as asked: the row is the large target and the
                            pencil is the discoverable one. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Edit ${a.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(a.id);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Delete ${a.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            del(a.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {/* Said out loud rather than left to be noticed. A management page that quietly
              shows a subset is how someone concludes an alert was never created and makes a
              second one. */}
          {hiddenCount > 0 && visibleAlerts.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {hiddenCount} more alert{hiddenCount === 1 ? "" : "s"} {hiddenCount === 1 ? "does" : "do"}{" "}
              not watch this selection and {hiddenCount === 1 ? "is" : "are"} hidden.
            </p>
          )}
        </CardContent>
      </Card>
    </ManageLimitsShell>
  );
}
